import { useState, useEffect, useCallback } from 'react';
import { loadCVData, saveCVData, defaultCVData } from '../services/cvStorage';

export const useCV = () => {
    const [cvData, setCvData] = useState(defaultCVData);
    const [template, setTemplate] = useState('modern');
    const [color, setColor] = useState('#2c3e50');
    const [font, setFont] = useState("'Inter', sans-serif");
    const [loading, setLoading] = useState(true);
    
    // Section order state
    const [sectionOrder, setSectionOrder] = useState([
        'summary',
        'education',
        'experience',
        'skills',
        'certifications',
        'languages'
    ]);

    // Load saved data on mount
    useEffect(() => {
        const savedData = loadCVData();
        setCvData(savedData);
        setLoading(false);
    }, []);

    // Save data whenever it changes
    useEffect(() => {
        if (!loading) {
            saveCVData(cvData);
        }
    }, [cvData, loading]);

    // Update personal info
    const updatePersonal = useCallback((field, value) => {
        setCvData(prev => ({
            ...prev,
            personal: { ...prev.personal, [field]: value }
        }));
    }, []);

    // Update summary
    const updateSummary = useCallback((value) => {
        setCvData(prev => ({ ...prev, summary: value }));
    }, []);

    // Update skills
    const updateSkills = useCallback((value) => {
        setCvData(prev => ({ ...prev, skills: value }));
    }, []);

    // Update certifications
    const updateCertifications = useCallback((value) => {
        setCvData(prev => ({ ...prev, certifications: value }));
    }, []);

    // Update languages
    const updateLanguages = useCallback((value) => {
        setCvData(prev => ({ ...prev, languages: value }));
    }, []);

    // Education CRUD
    const addEducation = useCallback(() => {
        const newEducation = {
            id: Date.now(),
            degree: '',
            institution: '',
            year: '',
            grade: ''
        };
        setCvData(prev => ({
            ...prev,
            education: [...prev.education, newEducation]
        }));
    }, []);

    const updateEducation = useCallback((id, field, value) => {
        setCvData(prev => ({
            ...prev,
            education: prev.education.map(edu =>
                edu.id === id ? { ...edu, [field]: value } : edu
            )
        }));
    }, []);

    const removeEducation = useCallback((id) => {
        setCvData(prev => ({
            ...prev,
            education: prev.education.filter(edu => edu.id !== id)
        }));
    }, []);

    // Experience CRUD
    const addExperience = useCallback(() => {
        const newExperience = {
            id: Date.now(),
            title: '',
            company: '',
            years: '',
            responsibilities: ''
        };
        setCvData(prev => ({
            ...prev,
            experience: [...prev.experience, newExperience]
        }));
    }, []);

    const updateExperience = useCallback((id, field, value) => {
        setCvData(prev => ({
            ...prev,
            experience: prev.experience.map(exp =>
                exp.id === id ? { ...exp, [field]: value } : exp
            )
        }));
    }, []);

    const removeExperience = useCallback((id) => {
        setCvData(prev => ({
            ...prev,
            experience: prev.experience.filter(exp => exp.id !== id)
        }));
    }, []);

    // Section order functions
    const moveSectionUp = useCallback((index) => {
        if (index === 0) return;
        setSectionOrder(prev => {
            const newOrder = [...prev];
            [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
            return newOrder;
        });
    }, []);

    const moveSectionDown = useCallback((index) => {
        if (index === sectionOrder.length - 1) return;
        setSectionOrder(prev => {
            const newOrder = [...prev];
            [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
            return newOrder;
        });
    }, [sectionOrder.length]);

    // Reset to defaults
    const resetToDefaults = useCallback(() => {
        setCvData(defaultCVData);
        setTemplate('modern');
        setColor('#2c3e50');
        setFont("'Inter', sans-serif");
        setSectionOrder([
            'summary',
            'education',
            'experience',
            'skills',
            'certifications',
            'languages'
        ]);
    }, []);

    return {
        cvData,
        template,
        setTemplate,
        color,
        setColor,
        font,
        setFont,
        loading,
        updatePersonal,
        updateSummary,
        updateSkills,
        updateCertifications,
        updateLanguages,
        education: cvData.education,
        addEducation,
        updateEducation,
        removeEducation,
        experience: cvData.experience,
        addExperience,
        updateExperience,
        removeExperience,
        resetToDefaults,
        sectionOrder,
        moveSectionUp,
        moveSectionDown
    };
};